import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
    FilePlus2, Save, Send, Copy, Archive, Trash2, Lock, Plus, X, ChevronLeft, RefreshCw,
} from 'lucide-react';

/**
 * RH · Avaliação de Desempenho — CONSTRUTOR DE MODELOS (versões por cargo).
 * Molde versionável (Rascunho → Publicado → congela ao ser usado; para mudar,
 * duplica), no padrão do construtor de chão do PPI. A escala 1–5 é fixa; o que
 * se monta são os BLOCOS (com peso) e os ITENS (competências). Cor RH #d81d1d.
 */
const API = '/api/rh/desempenho';
const CARGOS = [
    { abrev: 'COORD. QUAL E SERV', label: 'Coordenador Q&S' },
    { abrev: 'GERENTE PLANTAO', label: 'Gerente de Plantão' },
];
const authHeaders = () => ({ 'Content-Type': 'application/json', 'Authorization': `Bearer ${localStorage.getItem('authToken')}` });

const COR = '#d81d1d';
const TOM_STATUS = {
    Rascunho: { c: '#92400e', bg: '#fef3c7' },
    Publicado: { c: '#065f46', bg: '#d1fae5' },
    Arquivado: { c: '#475569', bg: '#f1f5f9' },
};

const RHDesempenhoVersoes = () => {
    const [versoes, setVersoes] = useState([]);
    const [filtroCargo, setFiltroCargo] = useState('');
    const [loading, setLoading] = useState(false);
    const [erro, setErro] = useState(null);
    const [editId, setEditId] = useState(null); // null = lista; 'new' = criar; número = editar

    const listar = useCallback(async () => {
        setLoading(true); setErro(null);
        try {
            const q = filtroCargo ? `?cargo=${encodeURIComponent(filtroCargo)}` : '';
            const res = await fetch(`${API}/versoes${q}`, { headers: authHeaders() });
            if (!res.ok) throw new Error('Erro ao listar modelos.');
            setVersoes(await res.json());
        } catch (e) { setErro(e.message); } finally { setLoading(false); }
    }, [filtroCargo]);

    useEffect(() => { if (editId === null) listar(); }, [editId, listar]);

    const acao = async (url, metodo = 'POST', okMsg) => {
        try {
            const res = await fetch(url, { method: metodo, headers: authHeaders() });
            const data = await res.json().catch(() => ({}));
            if (!res.ok) throw new Error(data.message || 'Erro na operação.');
            if (okMsg) window.alert(okMsg);
            listar();
        } catch (e) { window.alert(e.message); }
    };

    const th = { padding: '10px 14px', textAlign: 'left', fontSize: '11px', fontWeight: 700, textTransform: 'uppercase', color: '#8b6464', borderBottom: '2px solid #f0e2e2', whiteSpace: 'nowrap' };
    const td = { padding: '12px 14px', fontSize: '14px', color: '#1e293b', borderBottom: '1px solid #f9f1f1', verticalAlign: 'middle' };
    const btnIcon = (cor) => ({ display: 'inline-flex', alignItems: 'center', gap: '5px', padding: '6px 10px', borderRadius: '7px', border: `1px solid ${cor}22`, backgroundColor: `${cor}11`, color: cor, fontSize: '12px', fontWeight: 700, cursor: 'pointer' });

    if (editId !== null) {
        return <EditorVersao id={editId} onVoltar={() => setEditId(null)} />;
    }

    return (
        <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '12px', marginBottom: '16px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                    <span style={{ fontSize: '13px', fontWeight: 700, color: '#8b6464' }}>Cargo:</span>
                    <select value={filtroCargo} onChange={(e) => setFiltroCargo(e.target.value)}
                        style={{ padding: '7px 12px', borderRadius: '8px', border: '1px solid #f0e2e2', fontSize: '13px' }}>
                        <option value=''>Todos</option>
                        {CARGOS.map((c) => <option key={c.abrev} value={c.abrev}>{c.label}</option>)}
                    </select>
                    <button onClick={listar} style={btnIcon('#8b6464')}><RefreshCw size={14} /> Atualizar</button>
                </div>
                <button onClick={() => setEditId('new')}
                    style={{ display: 'inline-flex', alignItems: 'center', gap: '7px', padding: '9px 18px', borderRadius: '8px', border: 'none', backgroundColor: COR, color: '#fff', fontSize: '14px', fontWeight: 700, cursor: 'pointer' }}>
                    <FilePlus2 size={16} /> Novo modelo
                </button>
            </div>

            {erro && <div style={{ padding: '12px 16px', backgroundColor: '#fee2e2', borderRadius: '10px', color: '#dc2626', fontWeight: 600, marginBottom: '16px' }}>{erro}</div>}

            {loading ? <p style={{ color: '#8b6464' }}>Carregando…</p> : versoes.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '48px', color: '#8b6464' }}>Nenhum modelo. Crie o primeiro.</div>
            ) : (
                <div style={{ border: '1px solid #f0e2e2', borderRadius: '10px', overflow: 'hidden' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                        <thead><tr style={{ backgroundColor: '#fcf8f8' }}>
                            <th style={th}>Modelo</th><th style={th}>Cargo</th><th style={th}>Situação</th>
                            <th style={{ ...th, textAlign: 'center' }}>Usos</th><th style={{ ...th, textAlign: 'right' }}>Ações</th>
                        </tr></thead>
                        <tbody>
                            {versoes.map((v) => {
                                const cargo = CARGOS.find((c) => c.abrev === v.CARGO_ABREV);
                                const tom = TOM_STATUS[v.STATUS] || TOM_STATUS.Rascunho;
                                const congelada = v.STATUS !== 'Rascunho' && v.USOS > 0;
                                return (
                                    <tr key={v.ID}>
                                        <td style={{ ...td, fontWeight: 600 }}>
                                            {congelada && <Lock size={13} style={{ verticalAlign: 'middle', marginRight: '6px', color: '#94a3b8' }} />}
                                            {v.NOME_VERSAO}
                                        </td>
                                        <td style={td}>{cargo ? cargo.label : v.CARGO_ABREV}</td>
                                        <td style={td}><span style={{ padding: '3px 10px', borderRadius: '999px', fontSize: '12px', fontWeight: 700, color: tom.c, backgroundColor: tom.bg }}>{v.STATUS}</span></td>
                                        <td style={{ ...td, textAlign: 'center' }}>{v.USOS}</td>
                                        <td style={{ ...td, textAlign: 'right', whiteSpace: 'nowrap' }}>
                                            <div style={{ display: 'inline-flex', gap: '6px', flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                                                <button onClick={() => setEditId(v.ID)} style={btnIcon(congelada ? '#8b6464' : COR)}>{congelada ? 'Ver' : 'Editar'}</button>
                                                {v.STATUS === 'Rascunho' && <button onClick={() => acao(`${API}/versoes/${v.ID}/publicar`, 'POST', 'Modelo publicado.')} style={btnIcon('#065f46')}><Send size={13} /> Publicar</button>}
                                                <button onClick={() => acao(`${API}/versoes/${v.ID}/duplicar`, 'POST')} style={btnIcon('#1d4ed8')}><Copy size={13} /> Duplicar</button>
                                                {v.STATUS !== 'Arquivado' && <button onClick={() => acao(`${API}/versoes/${v.ID}/arquivar`, 'POST')} style={btnIcon('#475569')}><Archive size={13} /></button>}
                                                {v.USOS === 0 && <button onClick={() => { if (window.confirm('Apagar este modelo?')) acao(`${API}/versoes/${v.ID}`, 'DELETE'); }} style={btnIcon('#b91c1c')}><Trash2 size={13} /></button>}
                                            </div>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
};

// ─── Editor de uma versão (blocos → itens) ───────────────────────────────────
const EditorVersao = ({ id, onVoltar }) => {
    const criando = id === 'new';
    const [cargoAbrev, setCargoAbrev] = useState(CARGOS[0].abrev);
    const [nome, setNome] = useState('');
    const [descricao, setDescricao] = useState('');
    const [blocos, setBlocos] = useState([]);
    const [podeEditar, setPodeEditar] = useState(true);
    const [status, setStatus] = useState('Rascunho');
    const [carregando, setCarregando] = useState(!criando);
    const [salvando, setSalvando] = useState(false);

    useEffect(() => {
        if (criando) return;
        (async () => {
            try {
                const res = await fetch(`${API}/versoes/${id}`, { headers: authHeaders() });
                const data = await res.json();
                setCargoAbrev(data.versao.CARGO_ABREV); setNome(data.versao.NOME_VERSAO);
                setDescricao(data.versao.DESCRICAO || ''); setPodeEditar(data.podeEditar); setStatus(data.versao.STATUS);
                setBlocos((data.blocos || []).map((b) => ({
                    nome: b.NOME, peso: Number(b.PESO),
                    itens: (b.itens || []).map((i) => ({ codigo: i.CODIGO || '', rotulo: i.ROTULO || '', descricao: i.DESCRICAO || '' })),
                })));
            } catch { window.alert('Erro ao carregar o modelo.'); onVoltar(); }
            finally { setCarregando(false); }
        })();
    }, [id, criando, onVoltar]);

    const somaPesos = useMemo(() => blocos.reduce((s, b) => s + (Number(b.peso) || 0), 0), [blocos]);

    const setBloco = (bi, patch) => setBlocos((bs) => bs.map((b, i) => i === bi ? { ...b, ...patch } : b));
    const addBloco = () => setBlocos((bs) => [...bs, { nome: '', peso: 0, itens: [] }]);
    const delBloco = (bi) => setBlocos((bs) => bs.filter((_, i) => i !== bi));
    const setItem = (bi, ii, patch) => setBlocos((bs) => bs.map((b, i) => i !== bi ? b : { ...b, itens: b.itens.map((it, j) => j === ii ? { ...it, ...patch } : it) }));
    const addItem = (bi) => setBlocos((bs) => bs.map((b, i) => i !== bi ? b : { ...b, itens: [...b.itens, { codigo: '', rotulo: '', descricao: '' }] }));
    const delItem = (bi, ii) => setBlocos((bs) => bs.map((b, i) => i !== bi ? b : { ...b, itens: b.itens.filter((_, j) => j !== ii) }));

    const salvar = async () => {
        if (!nome.trim()) return window.alert('Dê um nome ao modelo.');
        setSalvando(true);
        try {
            let vid = id;
            if (criando) {
                const r = await fetch(`${API}/versoes`, { method: 'POST', headers: authHeaders(), body: JSON.stringify({ cargoAbrev, nomeVersao: nome, descricao }) });
                const d = await r.json(); if (!r.ok) throw new Error(d.message); vid = d.id;
            }
            const put = await fetch(`${API}/versoes/${vid}`, { method: 'PUT', headers: authHeaders(), body: JSON.stringify({ nomeVersao: nome, descricao, blocos }) });
            const pd = await put.json().catch(() => ({})); if (!put.ok) throw new Error(pd.message || 'Erro ao salvar.');
            window.alert('Modelo salvo.'); onVoltar();
        } catch (e) { window.alert(e.message); } finally { setSalvando(false); }
    };

    const inp = { width: '100%', padding: '8px 10px', borderRadius: '7px', border: '1px solid #f0e2e2', fontSize: '13px', boxSizing: 'border-box' };
    const label = { display: 'block', fontSize: '12px', fontWeight: 700, color: '#694747', marginBottom: '4px' };

    if (carregando) return <p style={{ color: '#8b6464' }}>Carregando…</p>;

    return (
        <div style={{ maxWidth: '860px' }}>
            <button onClick={onVoltar} style={{ display: 'inline-flex', alignItems: 'center', gap: '5px', border: 'none', background: 'none', color: '#8b6464', fontWeight: 600, cursor: 'pointer', marginBottom: '14px' }}><ChevronLeft size={16} /> Voltar aos modelos</button>

            {!podeEditar && (
                <div style={{ padding: '10px 14px', backgroundColor: '#ffedd5', border: '1px solid #fed7aa', borderRadius: '8px', color: '#9a3412', fontSize: '13px', fontWeight: 600, marginBottom: '14px' }}>
                    <Lock size={14} style={{ verticalAlign: 'middle', marginRight: '6px' }} /> Modelo {status.toLowerCase()} e já usado — somente leitura. Duplique para alterar.
                </div>
            )}

            <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', marginBottom: '16px' }}>
                <div style={{ flex: '1 1 220px' }}>
                    <label style={label}>Nome do modelo</label>
                    <input value={nome} onChange={(e) => setNome(e.target.value)} disabled={!podeEditar} style={inp} placeholder="Ex.: Avaliação de Desempenho — Coordenador (v2)" />
                </div>
                <div style={{ flex: '0 0 200px' }}>
                    <label style={label}>Cargo</label>
                    <select value={cargoAbrev} onChange={(e) => setCargoAbrev(e.target.value)} disabled={!criando} style={inp}>
                        {CARGOS.map((c) => <option key={c.abrev} value={c.abrev}>{c.label}</option>)}
                    </select>
                </div>
            </div>
            <div style={{ marginBottom: '16px' }}>
                <label style={label}>Descrição (opcional)</label>
                <input value={descricao} onChange={(e) => setDescricao(e.target.value)} disabled={!podeEditar} style={inp} />
            </div>

            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                <h4 style={{ margin: 0, fontSize: '15px', color: '#0f172a' }}>Blocos e itens</h4>
                <span style={{ fontSize: '13px', fontWeight: 700, color: Math.abs(somaPesos - 1) < 0.001 ? '#065f46' : '#b91c1c' }}>
                    Soma dos pesos: {(somaPesos * 100).toFixed(0)}% {Math.abs(somaPesos - 1) < 0.001 ? '✓' : '(deve ser 100%)'}
                </span>
            </div>

            {blocos.map((b, bi) => (
                <div key={bi} style={{ border: '1px solid #f0e2e2', borderRadius: '10px', padding: '14px', marginBottom: '14px', backgroundColor: '#fcf8f8' }}>
                    <div style={{ display: 'flex', gap: '10px', alignItems: 'flex-end', marginBottom: '12px', flexWrap: 'wrap' }}>
                        <div style={{ flex: '1 1 240px' }}>
                            <label style={label}>Nome do bloco</label>
                            <input value={b.nome} onChange={(e) => setBloco(bi, { nome: e.target.value })} disabled={!podeEditar} style={inp} placeholder="Ex.: Habilidade Pessoal" />
                        </div>
                        <div style={{ flex: '0 0 120px' }}>
                            <label style={label}>Peso (%)</label>
                            <input type="number" min="0" max="100" value={Math.round((Number(b.peso) || 0) * 100)} onChange={(e) => setBloco(bi, { peso: (Number(e.target.value) || 0) / 100 })} disabled={!podeEditar} style={inp} />
                        </div>
                        {podeEditar && <button onClick={() => delBloco(bi)} style={{ ...btnDel }}><Trash2 size={14} /></button>}
                    </div>

                    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                        <thead><tr>
                            <th style={{ ...thMini, width: '90px' }}>Código</th><th style={thMini}>Competência</th><th style={thMini}>Descrição</th><th style={{ ...thMini, width: '36px' }}></th>
                        </tr></thead>
                        <tbody>
                            {b.itens.map((it, ii) => (
                                <tr key={ii}>
                                    <td style={tdMini}><input value={it.codigo} onChange={(e) => setItem(bi, ii, { codigo: e.target.value })} disabled={!podeEditar} style={inp} /></td>
                                    <td style={tdMini}><input value={it.rotulo} onChange={(e) => setItem(bi, ii, { rotulo: e.target.value })} disabled={!podeEditar} style={inp} /></td>
                                    <td style={tdMini}><input value={it.descricao} onChange={(e) => setItem(bi, ii, { descricao: e.target.value })} disabled={!podeEditar} style={inp} /></td>
                                    <td style={tdMini}>{podeEditar && <button onClick={() => delItem(bi, ii)} style={{ border: 'none', background: 'none', color: '#b91c1c', cursor: 'pointer' }}><X size={16} /></button>}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                    {podeEditar && <button onClick={() => addItem(bi)} style={{ ...btnAdd, marginTop: '8px' }}><Plus size={14} /> Adicionar item</button>}
                </div>
            ))}

            {podeEditar && (
                <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', marginTop: '8px' }}>
                    <button onClick={addBloco} style={btnAdd}><Plus size={15} /> Adicionar bloco</button>
                    <button onClick={salvar} disabled={salvando} style={{ display: 'inline-flex', alignItems: 'center', gap: '7px', padding: '10px 20px', borderRadius: '8px', border: 'none', backgroundColor: salvando ? '#fd9393' : COR, color: '#fff', fontSize: '14px', fontWeight: 700, cursor: salvando ? 'not-allowed' : 'pointer' }}>
                        <Save size={16} /> {salvando ? 'Salvando…' : 'Salvar modelo'}
                    </button>
                </div>
            )}
        </div>
    );
};

const thMini = { padding: '6px 8px', textAlign: 'left', fontSize: '10px', fontWeight: 700, textTransform: 'uppercase', color: '#8b6464' };
const tdMini = { padding: '4px 6px', verticalAlign: 'middle' };
const btnAdd = { display: 'inline-flex', alignItems: 'center', gap: '6px', padding: '8px 14px', borderRadius: '8px', border: '1px dashed #d81d1d55', backgroundColor: '#fff', color: '#d81d1d', fontSize: '13px', fontWeight: 700, cursor: 'pointer' };
const btnDel = { display: 'inline-flex', alignItems: 'center', padding: '8px 10px', borderRadius: '8px', border: '1px solid #fecaca', backgroundColor: '#fff', color: '#b91c1c', cursor: 'pointer' };

export default RHDesempenhoVersoes;
